import { Link } from '@lt/components';
import { Trans, useTranslation } from '@lt/localization';
import { differenceInYears, isValid, parse, parseISO } from 'date-fns';
import React, { useMemo } from 'react';
import { CLIENT_DATE_FORMAT } from 'src/constants';
import { pluralizeYears } from 'src/utils/pluralizeUtils';
import {
  CHANGING_DAYS_LIMIT,
  ELDERLY_PERSON_THRESHOLD,
} from '../../contstants';
import type { DocType } from '../../types';
import type { Warnings } from './types';
import {
  TEST_ID_CHANGING_INFO_MESSAGE,
  TEST_ID_CERTIFICATE_MARK_MESSAGE,
  TEST_ID_CHILD_WITH_PASSPORT_MESSAGE,
  TEST_ID_ELDERLY_PERSON_MESSAGE,
  TEST_ID_VISA_MESSAGE,
} from './constants';
import styles from './touristsFormWarnings.module.css';

type Props = {
  toCountry: string;
  hasVisa: boolean;
  isInsuranceAvailable: boolean;
  endDate: string;
  docsLater: boolean;
  visaSupportAvailable: boolean;
  birthday: string;
  docType: DocType | null;
  isTravelToRussia: boolean;
};

const TouristsFormWarnings = (props: Props): JSX.Element => {
  const {
    toCountry,
    hasVisa,
    isInsuranceAvailable,
    endDate,
    docsLater,
    visaSupportAvailable,
    birthday,
    docType,
    isTravelToRussia,
  } = props;

  const { t } = useTranslation();
  const changingInfoWarning = t('checkout.touristForm.warning.change', {
    daysLimit: CHANGING_DAYS_LIMIT,
  });
  const elderlyPersonWarning = (
    <Trans
      i18nKey="checkout.touristForm.warning.elderly"
      values={{
        years: `${ELDERLY_PERSON_THRESHOLD} ${pluralizeYears(
          ELDERLY_PERSON_THRESHOLD,
        )}`,
      }}
    />
  );
  const visaWarning = t('checkout.touristForm.warning.visa', {
    country: toCountry,
  });
  const childBirthCertificateWarning = (
    <Trans
      i18nKey="checkout.touristForm.warning.childBirthCertificate"
      components={{
        Link: <Link href="https://help.level.travel/17/#rec576416571" />,
      }}
    />
  );
  const childWithPassportWarning = (
    <Trans
      i18nKey="checkout.touristForm.warning.childInParentsPassport"
      components={{
        Link: <Link href="https://help.level.travel/17/#rec576416571" />,
      }}
    />
  );

  const isPersonElderly = useMemo(() => {
    const birthdayDate = parse(birthday, CLIENT_DATE_FORMAT, new Date());
    const tourEndDate = parseISO(endDate);

    if ([birthdayDate, tourEndDate].some((date) => !isValid(date))) {
      return false;
    }

    const touristFullYearsToEndOfTour = differenceInYears(
      tourEndDate,
      birthdayDate,
    );

    return touristFullYearsToEndOfTour >= ELDERLY_PERSON_THRESHOLD;
  }, [birthday, endDate]);

  const childWithInternationalPassport = useMemo(
    () =>
      docType === 'international_passport' &&
      !isTravelToRussia &&
      differenceInYears(
        Date.now(),
        parse(birthday, CLIENT_DATE_FORMAT, new Date()),
      ) < 14,
    [birthday, docType, isTravelToRussia],
  );

  const warnings = useMemo<Warnings>(
    () => [
      {
        isOn: docsLater,
        text: changingInfoWarning,
        dataTestId: TEST_ID_CHANGING_INFO_MESSAGE,
      },
      {
        isOn: isInsuranceAvailable && isPersonElderly,
        component: elderlyPersonWarning,
        dataTestId: TEST_ID_ELDERLY_PERSON_MESSAGE,
      },
      {
        isOn: hasVisa && visaSupportAvailable,
        text: visaWarning,
        dataTestId: TEST_ID_VISA_MESSAGE,
      },
      {
        isOn: docType === 'birth_certificate' && !isTravelToRussia,
        component: childBirthCertificateWarning,
        dataTestId: TEST_ID_CERTIFICATE_MARK_MESSAGE,
      },
      {
        isOn: childWithInternationalPassport,
        component: childWithPassportWarning,
        dataTestId: TEST_ID_CHILD_WITH_PASSPORT_MESSAGE,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      docsLater,
      hasVisa,
      isInsuranceAvailable,
      isPersonElderly,
      visaSupportAvailable,
      docType,
      childWithInternationalPassport,
    ],
  );

  return (
    <>
      {warnings.map(
        ({ isOn, text, component, dataTestId }) =>
          isOn && (
            <div
              className={styles.container}
              key={text || component?.key}
              data-testid={dataTestId}
            >
              {text || component}
            </div>
          ),
      )}
    </>
  );
};

export default TouristsFormWarnings;
